import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { HOME } from "@/constants/testIds";
import { Menu, X, LogOut, ArrowRight } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const EXPLORE = [
  { label: "Your Needs", href: "#needs", id: HOME.navLinkNeeds },
  { label: "Coaching", href: "#coaching", id: HOME.navLinkPrograms },
  { label: "About", href: "#about", id: HOME.navLinkAbout },
  { label: "Reviews", href: "#reviews", id: HOME.navLinkReviews },
  { label: "FAQ", href: "#faq", id: HOME.navLinkFaq },
];

const OFFERINGS = [
  { label: "1-on-1 Sessions", href: "#coaching" },
  { label: "Group Circles", href: "#coaching" },
  { label: "Workshops", href: "#coaching" },
  { label: "Book a session", href: "#book" },
];

export default function Nav() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [open, setOpen] = useState(false);
  const onLanding = loc.pathname === "/";

  const goBook = () => {
    setOpen(false);
    if (onLanding) {
      document.getElementById("book")?.scrollIntoView({ behavior: "smooth" });
    } else {
      nav("/#book");
    }
  };

  const handleLogout = async () => { await logout(); setOpen(false); nav("/"); };

  return (
    <>
      <header className="sticky top-0 z-40" style={{ background: "rgba(241,234,225,0.92)", backdropFilter: "blur(10px)" }}>
        <div className="mx-auto max-w-[1200px] px-5 md:px-10 h-16 md:h-20 flex items-center justify-between border-b hairline">
          <Link to="/" data-testid={HOME.navBrand} className="font-serif-ev text-2xl md:text-3xl" style={{ color: "var(--ev-blue-2)" }}>
            Evolonef
          </Link>
          <nav className="hidden md:flex items-center gap-8 text-[15px]" style={{ color: "var(--ev-ink-2)" }}>
            {onLanding ? EXPLORE.map((e) => (
              <a key={e.label} data-testid={e.id} href={e.href} className="hover:opacity-70 transition">{e.label}</a>
            )) : <Link to="/" className="hover:opacity-70 transition">Home</Link>}
            {user && <Link data-testid={HOME.navLinkDashboard} to="/dashboard" className="hover:opacity-70 transition">Portal</Link>}
            {user?.role === "admin" && (
              <Link data-testid={HOME.navLinkAdmin} to="/admin" className="hover:opacity-70 transition" style={{ color: "var(--ev-blue-2)" }}>
                Admin
              </Link>
            )}
          </nav>
          <div className="flex items-center gap-2">
            <button
              onClick={goBook}
              data-testid={HOME.navBookBtn}
              className="hidden md:inline-flex ev-btn ev-btn-primary text-sm"
            >
              Book a free session
            </button>
            {user ? (
              <button data-testid={HOME.navLogoutBtn} onClick={handleLogout} className="hidden md:inline-flex ev-btn ev-btn-outline text-sm">
                <LogOut className="w-4 h-4" /> Sign out
              </button>
            ) : null}
            <button
              data-testid={HOME.navHamburger}
              aria-label="Open menu"
              onClick={() => setOpen(true)}
              className="w-11 h-11 grid place-items-center"
              style={{ color: "var(--ev-blue-2)" }}
            >
              <Menu className="w-6 h-6" />
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.aside
            data-testid={HOME.navDrawer}
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50"
            style={{ background: "var(--ev-bg)" }}
          >
            <div className="mx-auto max-w-[1200px] px-5 md:px-10 h-16 md:h-20 flex items-center justify-between border-b hairline">
              <span className="font-serif-ev text-2xl md:text-3xl" style={{ color: "var(--ev-blue-2)" }}>Evolonef</span>
              <button
                data-testid={HOME.navClose}
                onClick={() => setOpen(false)}
                aria-label="Close menu"
                className="w-11 h-11 grid place-items-center"
                style={{ color: "var(--ev-blue-2)" }}
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <motion.div
              initial={{ y: 10, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ duration: 0.35 }}
              className="mx-auto max-w-[1200px] px-6 md:px-10 py-10 grid grid-cols-1 md:grid-cols-3 gap-12"
            >
              <div>
                <div className="eyebrow mb-6">Explore</div>
                <ul className="space-y-5">
                  {EXPLORE.map((e) => (
                    <li key={e.label}>
                      <a href={e.href} onClick={() => setOpen(false)} className="text-2xl font-sans-ev">{e.label}</a>
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <div className="eyebrow mb-6">Offerings</div>
                <ul className="space-y-5">
                  {OFFERINGS.map((o) => (
                    <li key={o.label}>
                      <a href={o.href} onClick={() => setOpen(false)} className="text-2xl font-sans-ev">{o.label}</a>
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <div className="eyebrow mb-6">Portal</div>
                <ul className="space-y-5">
                  {user ? (
                    <>
                      <li><Link to="/dashboard" onClick={() => setOpen(false)} className="text-2xl font-sans-ev">Your Portal</Link></li>
                      {user.role === "admin" && (
                        <li>
                          <Link data-testid={HOME.navLinkAdmin} to="/admin" onClick={() => setOpen(false)}
                                className="text-2xl font-sans-ev" style={{ color: "var(--ev-blue-2)" }}>
                            Admin Console
                          </Link>
                        </li>
                      )}
                      <li><button data-testid={HOME.navLogoutBtn} onClick={handleLogout} className="text-2xl font-sans-ev">Sign out</button></li>
                    </>
                  ) : (
                    <>
                      <li><Link data-testid={HOME.navLoginBtn} to="/login" onClick={() => setOpen(false)} className="text-2xl font-sans-ev">Sign in</Link></li>
                      <li><Link data-testid={HOME.navRegisterBtn} to="/register" onClick={() => setOpen(false)} className="text-2xl font-sans-ev">Create account</Link></li>
                    </>
                  )}
                </ul>

                <div className="mt-10 space-y-3">
                  <button onClick={goBook} className="ev-btn ev-btn-primary w-full">
                    Book a free session <ArrowRight className="w-4 h-4" />
                  </button>
                  <div className="text-xs" style={{ color: "var(--ev-muted)" }}>No credit card required</div>
                </div>
              </div>
            </motion.div>
          </motion.aside>
        )}
      </AnimatePresence>
    </>
  );
}
